import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, UserCircle, Pencil, Trash2, UserX, UserCheck, KeyRound } from 'lucide-react';

// Role labels for display
const roleLabels = {
  admin: 'Admin',
  manager: 'Manager',
  standard: 'Standard',
  viewer: 'Viewer'
};

function UsersTab() {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [resetPinDialogOpen, setResetPinDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPin, setNewPin] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    pin: '',
    role: 'standard'
  });
  
  const [editFormData, setEditFormData] = useState({
    name: '',
    mobile: '',
    role: 'standard'
  });

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchUsers();
    }
  }, [currentUser]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data.users);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/users`, formData);
      toast.success(`User created! Temporary PIN: ${res.data.temp_pin}`);
      setDialogOpen(false);
      setFormData({ name: '', mobile: '', pin: '', role: 'standard' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      mobile: user.mobile,
      role: user.role
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, editFormData);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openDisableDialog = (user) => {
    setSelectedUser(user);
    setDisableDialogOpen(true);
  };

  const handleToggleDisable = async () => {
    try {
      const newStatus = !selectedUser.is_disabled;
      await axios.patch(`${API}/users/${selectedUser.id}/status`, { is_disabled: newStatus });
      toast.success(newStatus ? 'User disabled successfully' : 'User enabled successfully');
      setDisableDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const openResetPinDialog = (user) => {
    setSelectedUser(user);
    setNewPin('');
    setResetPinDialogOpen(true);
  };

  const handleResetPin = async () => {
    if (!newPin || newPin.length !== 6 || !/^\d+$/.test(newPin)) {
      toast.error('PIN must be exactly 6 digits');
      return;
    }
    try {
      await axios.post(`${API}/users/${selectedUser.id}/reset-pin`, { new_pin: newPin });
      toast.success(`PIN reset successfully! Temporary PIN: ${newPin}`);
      setResetPinDialogOpen(false);
      setSelectedUser(null);
      setNewPin('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset PIN');
    }
  };

  // Get display role label
  const getRoleLabel = (role) => roleLabels[role] || role;

  if (currentUser?.role !== 'admin') {
    return (
      <Card className="p-8 text-center border border-border">
        <p className="text-muted">You don't have permission to access user management</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-tab">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">User Management</h3>
          <p className="text-sm text-muted">Manage user accounts and access</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <Input
                  data-testid="user-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Mobile Number *</label>
                <Input
                  data-testid="user-mobile-input"
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Temporary PIN (6 digits) *</label>
                <Input
                  data-testid="user-pin-input"
                  type="password"
                  maxLength={6}
                  value={formData.pin}
                  onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                  required
                />
                <p className="text-xs text-muted mt-1">User will be required to change this on first login</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Role *</label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (Full Access)</SelectItem>
                    <SelectItem value="manager">Manager (Operational Access)</SelectItem>
                    <SelectItem value="standard">Standard (Basic Access)</SelectItem>
                    <SelectItem value="viewer">Viewer (Read Only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="user-submit-button">
                  Add User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name *</label>
              <Input
                data-testid="edit-user-name-input"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Mobile Number *</label>
              <Input
                data-testid="edit-user-mobile-input"
                type="tel"
                value={editFormData.mobile}
                onChange={(e) => setEditFormData({ ...editFormData, mobile: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Role *</label>
              <Select 
                value={editFormData.role} 
                onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}
              >
                <SelectTrigger data-testid="edit-user-role-select">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (Full Access)</SelectItem>
                  <SelectItem value="manager">Manager (Operational Access)</SelectItem>
                  <SelectItem value="standard">Standard (Basic Access)</SelectItem>
                  <SelectItem value="viewer">Viewer (Read Only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="edit-user-submit-button">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disable/Enable Confirmation Dialog */}
      <AlertDialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedUser?.is_disabled ? 'Enable User' : 'Disable User'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedUser?.is_disabled 
                ? `Are you sure you want to enable ${selectedUser?.name}? They will be able to log in again.`
                : `Are you sure you want to disable ${selectedUser?.name}? They will not be able to log in until re-enabled.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleToggleDisable}
              className={selectedUser?.is_disabled ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}
            >
              {selectedUser?.is_disabled ? 'Enable' : 'Disable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset PIN Dialog */}
      <Dialog open={resetPinDialogOpen} onOpenChange={setResetPinDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset PIN for {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted">
              Enter a new temporary PIN for this user. They will be required to change it on their next login.
            </p>
            <div>
              <label className="block text-sm font-medium mb-2">New Temporary PIN (6 digits) *</label>
              <Input
                data-testid="reset-pin-input"
                type="text"
                maxLength={6}
                placeholder="Enter 6-digit PIN"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setResetPinDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleResetPin}
                data-testid="reset-pin-submit-button"
                disabled={!newPin || newPin.length !== 6}
              >
                Reset PIN
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.map((user) => (
          <Card 
            key={user.id} 
            className={`p-6 border ${user.is_disabled ? 'border-red-200 bg-red-50/50 dark:bg-red-900/10 opacity-75' : 'border-border'}`} 
            data-testid="user-card"
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${user.is_disabled ? 'bg-gray-400' : 'bg-primary'}`}>
                <UserCircle className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold truncate">{user.name}</h3>
                    <p className="text-sm text-muted truncate">{user.mobile}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                    user.role === 'admin' ? 'bg-accent/10 text-accent' :
                    user.role === 'manager' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                    user.role === 'standard' ? 'bg-primary/10 text-primary' :
                    'bg-muted/10 text-muted'
                  }`}>
                    {getRoleLabel(user.role).toUpperCase()}
                  </span>
                  {user.is_disabled && (
                    <span className="inline-block px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                      DISABLED
                    </span>
                  )}
                </div>
                {user.is_first_login && !user.is_disabled && (
                  <p className="text-xs text-warning mt-2">Pending first login</p>
                )}
                
                {/* Action Buttons */}
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => openEditDialog(user)}
                    data-testid={`edit-user-${user.id}`}
                    title="Edit User"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => openResetPinDialog(user)}
                    data-testid={`reset-pin-${user.id}`}
                    title="Reset PIN"
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <KeyRound className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => openDisableDialog(user)}
                    data-testid={`disable-user-${user.id}`}
                    title={user.is_disabled ? 'Enable User' : 'Disable User'}
                    className={user.is_disabled ? 'text-green-600 hover:text-green-700' : 'text-orange-600 hover:text-orange-700'}
                  >
                    {user.is_disabled ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                  </Button>
                  {user.id !== currentUser?.id && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openDeleteDialog(user)}
                      data-testid={`delete-user-${user.id}`}
                      title="Delete User"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
        {users.length === 0 && (
          <Card className="p-8 text-center col-span-full">
            <UserCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-muted">No users found</p>
          </Card>
        )}
      </div>
    </div>
  );
}

export default UsersTab;
